import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ButtonBase from '@mui/material/ButtonBase';
import blueGrey from '@mui/material/colors/blueGrey';
import grey from '@mui/material/colors/grey';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import { Box } from '@mui/system';
import { useCallback, useRef } from 'react';
import { SortType } from '../../api/generated';
import { defineStyles } from '../../theme';
import { getColumnWidth, MIN_COLUMN_WIDTH } from '../constants/defaultColumnsWidth';
import { useMaxColumnWidth } from '../ColumnWidthsContext';
import { useSetUserColumnWidth, useUserColumnWidth } from '../UserColumnWidthsContext';
import { useSTColumn, useSTRows } from '../SmartTableContext';
import { cellStyle } from '../styles';
import TableHeaderMoreButton from './TableHeaderMoreButton';
import TableHeaderSort from './TableHeaderSort';
const styles = defineStyles({
    cellStyle: {
        ...cellStyle,
        borderRight: '1px solid',
        borderColor: blueGrey['100'],
        paddingRight: 0,
        '& h2': {
            fontSize: (theme) => theme.typography.subtitle1,
            fontWeight: 700,
            color: grey[900],
            my: 0,
        },
        '&:hover .table-header-more-button, &:focus .table-header-more-button': {
            opacity: 1,
            width: 28,
            ml: 0.5,
            paddingInline: 0.25,
        },
        '&:hover .table-header-resize-handle, &:focus .table-header-resize-handle': {
            backgroundColor: grey['500'],
        },
    },
    header: {
        display: 'flex',
        width: '100%',
    },
    headerText: {
        flex: 1,
        minWidth: 0,
    },
    notSortable: {
        '& .MuiButtonBase-root': {
            cursor: 'initial',
        },
    },
    sortIcon: {
        ml: 1,
        width: 24,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonBase: {
        display: 'block',
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    resizeHandle: {
        flexShrink: 0,
        width: 4,
        height: '100%',
        minHeight: 28,
        backgroundColor: 'transparent',
        transition: 'background-color 0.15s ease',
        cursor: 'col-resize',
    },
    cellContentWrapper: {
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'visible',
    },
});
export function TableHeaderCell({ cell, columnIndex }) {
    const { toggleSorting, setAsOnlySorting, columnSortingInfo } = useSTColumn(cell);
    const { rowsSelectEnabled } = useSTRows();
    const isIndex = columnIndex === 0;
    const isIpsosId = columnIndex === 1;
    const setUserWidth = useSetUserColumnWidth(cell.fieldName);
    const userWidth = useUserColumnWidth(cell.fieldName);
    const maxWidth = useMaxColumnWidth(cell.fieldName) ?? '100%';
    const width = getColumnWidth(cell.fieldName, cell.format, maxWidth, userWidth);
    const checkboxColWidth = 40;
    const indexColWidth = 56;
    const stickyBaseLeft = rowsSelectEnabled ? checkboxColWidth : 0;
    const stickyLeft = isIndex ? stickyBaseLeft : stickyBaseLeft + indexColWidth;
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);
    const handleResizeMouseDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const currentWidth = typeof width === 'number'
            ? width
            : e.currentTarget.closest('th')?.getBoundingClientRect().width ?? MIN_COLUMN_WIDTH;
        startXRef.current = startX;
        startWidthRef.current = currentWidth;
        const handleMouseMove = (moveEvent) => {
            const delta = moveEvent.clientX - startXRef.current;
            const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidthRef.current + delta);
            setUserWidth(newWidth);
        };
        const handleMouseUp = () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.removeProperty('user-select');
            document.body.style.removeProperty('cursor');
        };
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    }, [setUserWidth, width]);
    const handleSortClick = useCallback(() => {
        if (isIndex)
            return;
        if (columnSortingInfo) {
            toggleSorting();
            return;
        }
        setAsOnlySorting(SortType.Asc);
    }, [columnSortingInfo, setAsOnlySorting, isIndex, toggleSorting]);
    return (_jsx(TableCell, { sx: {
            ...styles.cellStyle,
            ...(isIndex && styles.notSortable),
            ...(!isIndex && { overflow: 'visible' }),
            ...(isIndex || isIpsosId
                ? {
                    position: 'sticky',
                    left: stickyLeft,
                    zIndex: 6,
                    backgroundColor: 'background.paper',
                    ...(isIpsosId
                        ? {
                            borderRight: '2px solid',
                            borderColor: 'divider',
                        }
                        : {}),
                }
                : {}),
            width,
            minWidth: width,
            maxWidth: width,
        }, width: isIndex ? undefined : width, children: _jsxs(Box, { sx: !isIndex ? [styles.header, styles.cellContentWrapper] : styles.header, children: [_jsx(Box, { sx: styles.headerText, children: _jsx(Tooltip, { title: cell.header, children: _jsx(ButtonBase, { disableRipple: true, onClick: handleSortClick, sx: {
                                width: '100%',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                            }, children: _jsx(Box, { component: "span", sx: styles.buttonBase, children: cell.header }) }) }) }), _jsx(TableHeaderSort, { cell: cell, onClickSort: handleSortClick }), _jsx(TableHeaderMoreButton, { cell: cell }), !isIndex && (_jsx(Box, { component: "div", role: "separator", "aria-label": "Resize column", "aria-orientation": "vertical", className: "table-header-resize-handle", sx: styles.resizeHandle, onMouseDown: handleResizeMouseDown }))] }) }, `table-cell-${cell.fieldName}`));
}
//# sourceMappingURL=TableHeaderCell.js.map